<template>
  <div class="relative" ref="dropdownContainer">
    <button
      @click.stop="onPrimaryButtonClick"
      type="button"
      :disabled="xlsxOnly && isGeneratingXlsx"
      :class="[
        'inline-flex items-center gap-2 whitespace-nowrap rounded-lg font-sans text-sm font-semibold leading-5 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2',
        buttonVariant === 'export'
          ? 'bg-gray-modern-100 px-3.5 py-2.5 text-brand-600 hover:bg-gray-modern-200'
          : 'btn-brand px-3.5 py-2.5 text-white',
      ]"
    >
      <DownloadIcon
        v-if="buttonVariant === 'export'"
        class="h-[17px] w-[17px] shrink-0"
      />
      <svg
        v-else
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="shrink-0"
      >
        <path
          d="M6.66667 11.6667C7.58714 11.6667 8.33333 10.9205 8.33333 10C8.33333 9.07953 7.58714 8.33333 6.66667 8.33333C5.74619 8.33333 5 9.07953 5 10C5 10.9205 5.74619 11.6667 6.66667 11.6667Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M13.3333 7.5C14.2538 7.5 15 6.75381 15 5.83333C15 4.91286 14.2538 4.16667 13.3333 4.16667C12.4129 4.16667 11.6667 4.91286 11.6667 5.83333C11.6667 6.75381 12.4129 7.5 13.3333 7.5Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M13.3333 15.8333C14.2538 15.8333 15 15.0871 15 14.1667C15 13.2462 14.2538 12.5 13.3333 12.5C12.4129 12.5 11.6667 13.2462 11.6667 14.1667C11.6667 15.0871 12.4129 15.8333 13.3333 15.8333Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.08333 11.0417L11.9167 13.125"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M11.9167 6.875L8.08333 8.95833"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span v-if="buttonLabel">{{
        xlsxOnly && isGeneratingXlsx ? "Generating…" : buttonLabel
      }}</span>
      <Translation
        v-else
        :name="buttonTranslationName"
        :default="buttonTranslationDefault"
      />
      <svg
        v-if="!xlsxOnly"
        class="shrink-0 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 7.5L10 12.5L15 7.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <p
      v-if="xlsxOnly && xlsxErrorMessage"
      class="absolute right-0 top-full mt-2 w-48 text-xs font-semibold text-red-600"
    >
      {{ xlsxErrorMessage }}
    </p>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen && !xlsxOnly"
      class="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
    >
      <button
        v-if="showPdfExport"
        type="button"
        @click="requestPdfExport"
        :disabled="isGeneratingPdf"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center"
      >
        <span class="mr-3 text-gray-600">
          <DownloadIcon />
        </span>
        <div>
          <Translation
            v-if="isGeneratingPdf"
            name="report.generating_pdf"
            default="Generating PDF..."
          />
          <Translation
            v-else
            name="report.pdf_export"
            default="Export to PDF"
          />
        </div>
      </button>

      <p
        v-if="pdfErrorMessage"
        class="px-4 pb-2 text-xs font-semibold text-red-600"
      >
        {{ pdfErrorMessage }}
      </p>

      <button
        type="button"
        @click="requestXlsxExport"
        :disabled="isGeneratingXlsx"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center"
      >
        <span class="mr-3 text-gray-600">
          <DownloadIcon />
        </span>
        <div>
          <Translation
            v-if="isGeneratingXlsx"
            name="report.generating_xlsx"
            default="Generating XLSX..."
          />
          <Translation
            v-else
            name="report.xlsx_export"
            default="Export to XLSX"
          />
        </div>
      </button>

      <p
        v-if="xlsxErrorMessage"
        class="px-4 pb-2 text-xs font-semibold text-red-600"
      >
        {{ xlsxErrorMessage }}
      </p>

      <button
        v-if="extraXlsxExport"
        type="button"
        @click="requestExtraXlsxExport"
        :disabled="isGeneratingExtraXlsx"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center"
      >
        <span class="mr-3 text-gray-600">
          <DownloadIcon />
        </span>
        <div>
          <Translation
            v-if="isGeneratingExtraXlsx"
            name="report.generating_xlsx"
            default="Generating XLSX..."
          />
          <Translation
            v-else
            :name="extraXlsxExport.translation_name"
            :default="extraXlsxExport.translation_default"
          />
        </div>
      </button>

      <p
        v-if="extraXlsxErrorMessage"
        class="px-4 pb-2 text-xs font-semibold text-red-600"
      >
        {{ extraXlsxErrorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from "vue";
import DownloadIcon from "./icons/DownloadIcon.vue";
import { useExportPoller } from "../utils/useExportPoller";

function normalizeExportUrls(raw) {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
}

export default {
  name: "ShareReportDropdown",
  components: { DownloadIcon },
  props: {
    exportUrls: {
      type: [Object, Array, String],
      required: true,
    },
    buttonVariant: {
      type: String,
      default: "share",
    },
    buttonTranslationName: {
      type: String,
      default: "report.share_report",
    },
    buttonTranslationDefault: {
      type: String,
      default: "Share Report",
    },
    // Plain label (skips the Translation component). Used on ActiveAdmin pages
    // where the AFT translation provider is not mounted.
    buttonLabel: {
      type: String,
      default: "",
    },
    // When true, the export-variant trigger starts an XLSX download directly
    // (no dropdown). Matches the buyer export button look without PDF.
    xlsxOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isOpen = ref(false);
    const dropdownContainer = ref(null);

    const exportUrlsResolved = computed(() =>
      normalizeExportUrls(props.exportUrls),
    );

    const showPdfExport = computed(
      () =>
        !props.xlsxOnly &&
        exportUrlsResolved.value?.pdf_export_url?.url != null,
    );

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const pdfPoller = useExportPoller({
      getUrls: () => exportUrlsResolved.value?.pdf_export_url,
      errorLabel: "PDF generation failed. Please try again.",
      closeDropdown,
    });

    const xlsxPoller = useExportPoller({
      getUrls: () => exportUrlsResolved.value?.xlsx_export_url,
      errorLabel: "XLSX generation failed. Please try again.",
      closeDropdown,
    });

    const extraXlsxExport = computed(() =>
      props.xlsxOnly ? null : exportUrlsResolved.value?.extra_xlsx_export_url,
    );

    const extraXlsxPoller = useExportPoller({
      getUrls: () => extraXlsxExport.value,
      errorLabel: "XLSX generation failed. Please try again.",
      closeDropdown,
    });

    const onPrimaryButtonClick = () => {
      if (props.xlsxOnly) {
        xlsxPoller.requestExport();
        return;
      }
      toggleDropdown();
    };

    const handleClickOutside = (event) => {
      if (
        dropdownContainer.value &&
        !dropdownContainer.value.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      if (!props.xlsxOnly) pdfPoller.checkStatusOnLoad();
      xlsxPoller.checkStatusOnLoad();
      extraXlsxPoller.checkStatusOnLoad();
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
      pdfPoller.stopPolling();
      xlsxPoller.stopPolling();
      extraXlsxPoller.stopPolling();
    });

    return {
      isOpen,
      dropdownContainer,
      toggleDropdown,
      onPrimaryButtonClick,
      showPdfExport,
      isGeneratingPdf: pdfPoller.isGenerating,
      pdfErrorMessage: pdfPoller.errorMessage,
      requestPdfExport: pdfPoller.requestExport,
      isGeneratingXlsx: xlsxPoller.isGenerating,
      xlsxErrorMessage: xlsxPoller.errorMessage,
      requestXlsxExport: xlsxPoller.requestExport,
      extraXlsxExport,
      isGeneratingExtraXlsx: extraXlsxPoller.isGenerating,
      extraXlsxErrorMessage: extraXlsxPoller.errorMessage,
      requestExtraXlsxExport: extraXlsxPoller.requestExport,
    };
  },
};
</script>
